<div mat-dialog-title class="modal-header">
  {{ isMetric ? 'Edit Metric' : 'Edit Chart' }}
</div>

@if (form) {
  <mat-dialog-content [formGroup]="form" class="p-t-20 mat-typography">
    <div class="m-b-15 center">
      <mat-button-toggle-group
        class="toggle-group-compact card-type-toggle"
        formControlName="cardType"
        aria-label="Card type"
        [hideSingleSelectionIndicator]="true">
        <span
          class="toggle-tooltip-trigger"
          [matTooltip]="isExtraSmallCard ? 'Extra-small metrics cannot be switched to charts.' : ''"
          [attr.tabindex]="isExtraSmallCard ? 0 : null">
          <mat-button-toggle [value]="CardType.Chart" [disabled]="isExtraSmallCard">
            <mat-icon class="toggle-option-icon">insert_chart</mat-icon>
            <span>Chart</span>
          </mat-button-toggle>
        </span>
        <span
          class="toggle-tooltip-trigger"
          [matTooltip]="
            metricTypeDisabled ? 'The selected datasource does not support metrics.' : ''
          "
          [attr.tabindex]="metricTypeDisabled ? 0 : null">
          <mat-button-toggle [value]="CardType.Metric" [disabled]="metricTypeDisabled">
            <mat-icon class="toggle-option-icon">speed</mat-icon>
            <span>Metric</span>
          </mat-button-toggle>
        </span>
      </mat-button-toggle-group>
      @if (willReduceCardSize) {
        <div class="card-size-warning">
          <mat-icon>warning</mat-icon>
          <span>This card will be reduced in size when saved as a metric.</span>
        </div>
      }
    </div>
    <mat-form-field
      appearance="fill"
      class="full-width"
      [ngClass]="{ err: (input?.value?.length ?? 0) > MAX_NAME_LEN }">
      <mat-label>Name</mat-label>
      <input #input formControlName="name" matInput cdkFocusInitial />
      <mat-hint align="end" [ngClass]="{ err: input.value.length > MAX_NAME_LEN }">
        {{ input.value.length || 0 }}/{{ MAX_NAME_LEN }}
      </mat-hint>
    </mat-form-field>
    @if (isExtraSmallCard && input.value && input.value.length > TITLE_OVERFLOW_WARN_LEN) {
      <div class="title-overflow-warning">
        <mat-icon>warning</mat-icon>
        <span
          >Name may overflow on certain screen widths at {{ TITLE_OVERFLOW_WARN_LEN + 1 }}+
          characters</span
        >
      </div>
    }
    @if (isMetric) {
      <div class="m-b-15 center">
        <mat-button-toggle-group
          class="toggle-group-compact"
          formControlName="metricDisplayType"
          aria-label="Display type"
          [hideSingleSelectionIndicator]="true">
          <mat-button-toggle value="numeric">
            <mat-icon class="toggle-option-icon display-option-icon number-option-icon"
              >123</mat-icon
            >
            <span>Number</span>
          </mat-button-toggle>
          <mat-button-toggle value="gauge">
            <mat-icon class="toggle-option-icon display-option-icon">speed</mat-icon>
            <span>Gauge</span>
          </mat-button-toggle>
        </mat-button-toggle-group>
      </div>
    }
    <mat-form-field appearance="fill" class="full-width m-t-10">
      <mat-label>Datasource</mat-label>
      <mat-select formControlName="datasource">
        @for (source of datasourceOptions; track source) {
          <mat-option [value]="source">
            {{ source | datasourceNamePipe }}
          </mat-option>
        }
      </mat-select>
    </mat-form-field>
    @if (dataSourceFilterFields) {
      <div [ngClass]="{ section: showTask2Fields }">
        @if (isTaskSource) {
          @if (showTaskSelectionMode) {
            <div class="m-b-15 center">
              <mat-button-toggle-group
                class="toggle-group-compact"
                formControlName="taskMode"
                aria-label="Task source">
                <mat-button-toggle value="taskType">Task Type</mat-button-toggle>
                <mat-button-toggle value="taskCode">Task Code</mat-button-toggle>
              </mat-button-toggle-group>
            </div>
          }
          @if (
            showTaskSelectionMode && form.controls.taskMode.value === 'taskCode' && taskCodes.length
          ) {
            <mat-form-field
              appearance="fill"
              class="full-width"
              [ngClass]="{ 'm-b-20': !showTask1DateField }">
              <mat-label>Task Code</mat-label>
              <mat-select formControlName="taskCode">
                <mat-option>
                  <ngx-mat-select-search
                    [formControl]="taskCodeSearchCtrl"
                    placeholderLabel="Search..."
                    noEntriesFoundLabel="No task codes found"></ngx-mat-select-search>
                </mat-option>
                @for (taskCode of filteredTaskCodes; track taskCode) {
                  <mat-option [value]="taskCode">{{ taskCode }}</mat-option>
                }
              </mat-select>
              <mat-hint>All task types sharing this code will be included.</mat-hint>
            </mat-form-field>
          }
          @if (
            (!showTaskSelectionMode || form.controls.taskMode.value === 'taskType') &&
            taskTypeGroups.length
          ) {
            <mat-form-field
              appearance="fill"
              class="full-width"
              [ngClass]="{ 'm-b-20': !showTask1DateField && !isJobsBetweenTasks }">
              <mat-label>{{ firstTaskLabel }}</mat-label>
              <mat-select formControlName="taskType">
                <mat-option>
                  <ngx-mat-select-search
                    [formControl]="taskTypeSearchCtrl"
                    placeholderLabel="Search..."
                    noEntriesFoundLabel="No tasks found"></ngx-mat-select-search>
                </mat-option>
                @for (group of filteredTaskTypeGroups; track group.groupName) {
                  <mat-optgroup [label]="group.groupName">
                    @for (task of group.tasks; track task.id) {
                      <mat-option [value]="task.id">{{ task.taskTitle }}</mat-option>
                    }
                  </mat-optgroup>
                }
              </mat-select>
            </mat-form-field>
          }
          @if (
            isTaskSource &&
            ((form.controls.taskMode.value === 'taskCode' && loading) ||
              (form.controls.taskMode.value !== 'taskCode' && !taskTypeGroups.length)) &&
            !tasksError
          ) {
            <div class="task-spinner" [ngClass]="{ 'm-b-20': !showTask1DateField }">
              <app-spinner [inline]="true" heightPx="78"></app-spinner>
            </div>
          }
          @if (
            isTaskSource &&
            form.controls.taskMode.value === 'taskCode' &&
            !loading &&
            !taskCodes.length &&
            !tasksError
          ) {
            <div class="info-box-sml m-b-20">No task codes have been configured.</div>
          }
          @if (tasksError) {
            <div class="error-box-sml">
              There was an error loading the tasks. Please try again in a moment.
            </div>
          }
        }
        @if (showTask1DateField) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Task 1 date field</mat-label>
            <mat-select formControlName="task1DateField">
              @for (df of dateFilterFieldOptions; track df) {
                <mat-option [value]="df.dataField">
                  {{ df.displayName }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
        @if (showTask2Fields && taskTypeGroups.length) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>{{ secondTaskLabel }}</mat-label>
            <mat-select formControlName="taskType2">
              <mat-option>
                <ngx-mat-select-search
                  [formControl]="taskTypeSearchCtrl"
                  placeholderLabel="Search..."
                  noEntriesFoundLabel="No tasks found"></ngx-mat-select-search>
              </mat-option>
              @if (!isJobsBetweenTasks) {
                <mat-option [value]="null">-- None --</mat-option>
              }
              @for (group of filteredTaskTypeGroups; track group.groupName) {
                <mat-optgroup [label]="group.groupName">
                  @for (task of group.tasks; track task.id) {
                    <mat-option [value]="task.id">{{ task.taskTitle }}</mat-option>
                  }
                </mat-optgroup>
              }
            </mat-select>
          </mat-form-field>
        }
        @if (showTask2Fields && !taskTypeGroups.length && !tasksError) {
          <div class="task-spinner">
            <app-spinner [inline]="true" heightPx="48"></app-spinner>
          </div>
        }
        @if (showTask2Fields && tasksError) {
          <div class="error-box-sml">
            There was an error loading the tasks. Please try again in a moment.
          </div>
        }
        @if (showTask1DateField) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Task 2 date Field</mat-label>
            <mat-select formControlName="task2DateField">
              @for (df of dateFilterFieldOptions; track df) {
                <mat-option [value]="df.dataField">
                  {{ df.displayName }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
      </div>
      <div class="section">
        @if (showValueField) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Value Field (number attribute to graph on Y axis)</mat-label>
            <mat-select formControlName="valueField">
              @for (vf of valueFieldOptions; track vf) {
                <mat-option [value]="vf.dataField">
                  {{ vf.displayName }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
        @if (showTrackingField) {
          @if (!loadingTrackingFields) {
            <mat-form-field appearance="fill" class="full-width">
              <mat-label>Tracking Field</mat-label>
              <mat-select formControlName="trackingField">
                @for (tf of trackingFieldOptions; track tf) {
                  <mat-option [value]="tf.dataField">
                    {{ tf.displayName }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
          @if (loadingTrackingFields) {
            <div class="info-box-sml m-b-15 p-t-25 p-b-25">Loading tracking fields...</div>
          }
        }
        @if (showDurationAggregation) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Duration Unit</mat-label>
            <mat-select formControlName="taskDurationOption">
              @for (option of taskDurationOptions; track option) {
                <mat-option [value]="option">
                  {{ option }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
        @if (showValueAggregation) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Value Aggregation Type</mat-label>
            <mat-select formControlName="valueAggregation">
              @for (aggregation of aggregationOptions; track aggregation) {
                <mat-option [value]="aggregation">
                  {{ aggregation }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
        @if (cumulativeAggregation && !hideStartingValue) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Starting Value (ignored if graphing multiple series)</mat-label>
            <input formControlName="startingValue" type="number" step="any" matInput />
          </mat-form-field>
        }
          @if (showArgumentFIeld) {
            <mat-form-field appearance="fill" class="full-width">
              <mat-label>Argument Field (attribute to group-by on X axis)</mat-label>
              <mat-select formControlName="argumentField">
                @if (argFieldOptionGroups.length) {
                  @for (group of argFieldOptionGroups; track group.label) {
                    <mat-optgroup [label]="group.label">
                      @for (af of group.fields; track af.dataField) {
                        <mat-option [value]="af.dataField">
                          {{ af.displayName }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                } @else {
                  @for (af of argFieldOptions; track af.dataField) {
                    <mat-option [value]="af.dataField">
                      {{ af.displayName }}
                    </mat-option>
                  }
                }
              </mat-select>
            </mat-form-field>
          }
          @if (argFieldIsDateType) {
            <mat-form-field appearance="fill" class="full-width">
              <mat-label>Argument Date Grouping</mat-label>
              <mat-select formControlName="grouping">
                @for (group of groupOptions; track group) {
                  <mat-option [value]="group">
                    {{ group }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
          @if (showGroupBySeries) {
            <mat-form-field appearance="fill" class="full-width">
              <mat-label>Graph separate series split by</mat-label>
              <mat-select formControlName="splitByField" [compareWith]="compareFields">
                @if (seriesOptionGroups.length) {
                  <mat-option [value]="DISABLED_SEPARATE_SERIES_OPTION">
                    {{ DISABLED_SEPARATE_SERIES_OPTION.displayName }}
                  </mat-option>
                  @for (group of seriesOptionGroups; track group.label) {
                    <mat-optgroup [label]="group.label">
                      @for (series of group.fields; track series.dataField) {
                        <mat-option [value]="series">
                          {{ series.displayName }}
                        </mat-option>
                      }
                    </mat-optgroup>
                  }
                } @else {
                  @for (series of seriesOptions; track series.dataField) {
                    <mat-option [value]="series">
                      {{ series.displayName }}
                    </mat-option>
                  }
                }
              </mat-select>
            </mat-form-field>
          }
          @if (hasMetricSplit) {
            <mat-form-field appearance="fill" class="full-width">
              <mat-label>Split series displayed</mat-label>
              <mat-select formControlName="metricSplitDisplayCount">
                @for (count of metricSplitDisplayCountOptions; track count) {
                  <mat-option [value]="count">{{ count }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="fill" class="full-width">
              <mat-label>Split-series subset (max of 8 groups)</mat-label>
              <mat-select formControlName="metricSplitSubsetMode">
                @for (option of metricSplitSubsetOptions; track option.value) {
                  <mat-option
                    [value]="option.value"
                    [disabled]="option.requiresTarget && form.controls.targetValue.value === null">
                    {{ option.label }}
                  </mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
          @if (showShowFilteredCategoriesOnly) {
            <div class="m-b-15 p-r-5 flex-row-start-center">
              <mat-checkbox
                formControlName="showFilteredCategoriesOnly"
                class="checkbox"></mat-checkbox>
              <span class="m-l-8">
                {{ screen.isXSmall ? 'Filtered categories only' : 'Show filtered categories only' }}
              </span>
              <mat-icon id="filteredCategoriesId" class="help-icon">info_outline</mat-icon>
              <dx-tooltip
                target="#filteredCategoriesId"
                container=".edit-card-dialog"
                showEvent="mouseenter"
                hideEvent="mouseleave"
                position="top">
                <p><b>Only show categories/options that occur in the filtered data.</b></p>
                <p>
                  This applies to non-date x-axis groupings and separate-series splitting
                  categories,
                  <br />
                  and only applies if a date filter or custom filter is applied.
                </p>
                <p>
                  E.G. if grouping by 'House Type', splitting by 'Division' and filtering to the
                  last month, <br />
                  only the House Types that occur in the last month will be shown as x-axis
                  groupings/categories, <br />
                  and only Divisions that occur in the last month will be shown as series in the
                  legend.
                </p>
                <p>
                  DISABLED BENEFIT<br />
                  Accurately shows all possible options i.e. all Divisions in the company<br />
                  Colours will be consistent across charts with the same dataset
                </p>
                <p>
                  ENABLED BENEFIT<br />
                  Reduces noise (empty categories) if filtering by a narrow time range <br />
                  or explicitly filtering out categories
                </p>
              </dx-tooltip>
            </div>
          }
          @if (showExcludeInactive) {
            <div
              class="m-b-15 p-r-5 flex-row-start-center"
              [ngClass]="{ 'nm-t-15': showShowFilteredCategoriesOnly }">
              <mat-checkbox formControlName="excludeInactiveUsers" class="checkbox"></mat-checkbox>
              <span class="m-l-8"> Exclude inactive users </span>
              <mat-icon id="excludeInactiveId" class="help-icon">info_outline</mat-icon>
              <dx-tooltip
                target="#excludeInactiveId"
                container=".edit-card-dialog"
                showEvent="mouseenter"
                hideEvent="mouseleave"
                position="top">
                <p><b>Exclude inactive users when grouping-by or splitting-on a user field</b></p>
                <p>
                  When the chart is grouped-by or split-on (graphing separate series) by a person
                  field (e.g. Sales Rep, Site Manager), <br />this will hide data for any person who
                  is no longer active for the company.
                </p>
              </dx-tooltip>
            </div>
          }
        @if (unavailableFieldsInUse.length) {
          <div class="error-box-sml m-b-15">
            No longer available: {{ unavailableFieldsInUse.join(', ') }}.
            Choose different fields or remove those filter conditions to save.
          </div>
        }
      </div>
      <div class="section">
        <mat-form-field appearance="fill" class="full-width">
          <mat-label>Date Range</mat-label>
          <mat-select formControlName="rangeType">
            @for (range of rangeOptions; track range) {
              <mat-option
                [value]="range"
                [disabled]="rangeInvalidForGrouping(range)"
                [matTooltip]="
                  rangeInvalidForGrouping(range)
                    ? 'The current Grouping does not allow this option'
                    : ''
                ">
                {{ range }}
              </mat-option>
            }
          </mat-select>
        </mat-form-field>
        @if (showDateFilterField) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Date Filter Field</mat-label>
            <mat-select formControlName="dateFilterField">
              @for (df of dateFilterFieldOptions; track df) {
                <mat-option [value]="df.dataField">
                  {{ df.displayName }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
          <div class="m-b-15 p-r-5 flex-row-start-center">
            <mat-checkbox
              formControlName="includeRecordsWithNoDate"
              class="checkbox"></mat-checkbox>
            <span class="m-l-8">Include records with no {{ selectedDateFilterFieldName }}</span>
          </div>
        }
        <div class="flex justify-between responsive-date-fields">
          @if (form.controls.rangeType.value !== 'All Time') {
            <mat-form-field appearance="fill" class="half-field">
              <mat-label>
                {{ isPresetDateRange ? 'Example (current) Range' : 'Custom Date Range' }}
              </mat-label>
              <mat-date-range-input
                [rangePicker]="picker"
                [disabled]="form.controls.rangeType.value !== 'Custom'">
                <input matStartDate formControlName="start" placeholder="Start date" />
                <input matEndDate formControlName="end" placeholder="End date" />
              </mat-date-range-input>
              <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
              <mat-date-range-picker #picker></mat-date-range-picker>
            </mat-form-field>
          }
          @if (isPresetDateRange) {
            <div class="include-current-period">
              <mat-checkbox
                formControlName="includeCurrentPartialPeriod"
                class="checkbox"></mat-checkbox>
              <span class="m-l-8">
                {{
                  screen.isXSmall ? 'Include Current period' : 'Include current (partial) period'
                }}
              </span>
              <mat-icon id="partialPeriodHelpId" class="m-l-8 help-icon">info_outline</mat-icon>
              <dx-tooltip
                target="#partialPeriodHelpId"
                container=".edit-card-dialog"
                showEvent="mouseenter"
                hideEvent="mouseleave"
                position="top">
                <p>
                  <b>Include the current period (week/month/year) in the data</b>
                  <br />When in doubt check the provided example range
                </p>
                <br />
                <p class="m-t-130">FUTURE DATE RANGES:</p>
                <p>
                  <b>ENABLED:</b> data starts from the start of the current period <br />! Note some
                  data sources may not have data before the current day !
                </p>
                <p><b>DISABLED:</b> data starts from the start of the next period</p>
                <br />
                <p class="m-t-15">PAST DATE RANGES:</p>
                <p>
                  <b>ENABLED:</b> data goes up to the current day <br />! Chart data will change
                  every day ! <br />If grouping by date, only the data in the last/current period
                  will change daily <br />If NOT grouping by date, all values may change every day
                </p>
                <p>
                  <b>DISABLED:</b> data goes up to the end of the previous period <br />Values are
                  stable until the date ticks over into the next period
                </p>
              </dx-tooltip>
            </div>
          }
        </div>
        @if (rangeNotAlignToGrouping) {
          <div
            [ngClass]="{
              'warn-box-sml': !form.hasError('rollingPeriodRangeError'),
              'error-box-sml': form.hasError('rollingPeriodRangeError'),
            }"
            class="m-t-10 m-b-10">
            The custom range does not align to the selected date grouping.
            @if (!usingRollingPeriods) {
              <div>Values in the first and/or last periods may be unexpected.</div>
            }
            @if (usingRollingPeriods) {
              <div>This is not allowed with rolling period averages.</div>
            }
          </div>
        }
        <!-- <div *ngIf="dynamicDateRange" class="info-box-sml m-b-5">
          NOTE: The range will be adjusted to fit the current grouping.
          <br> The start date will be moved forward to align to the start of the {{currentGroupingPeriod()}}
          </div> -->
      </div>
      <div class="section m-b-20 target-config-section">
        @if (!isMetric) {
          <mat-form-field appearance="fill" class="full-width">
            <mat-label>Chart Type</mat-label>
            <mat-select formControlName="chartType">
              @for (chart of chartOptions; track chart) {
                <mat-option [value]="chart">
                  {{ chart }}
                </mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
        @if (!hideChartColorSelection) {
          <div
            class="dx-field m-b-20"
            [matTooltip]="
              chartColourDisabled
                ? 'With a default target value, colours are controlled by the status against the target.'
                : ''
            ">
            <div class="dx-field-label color-label" style="width: 25%">Chart Colour</div>
            <div class="dx-field-value" style="width: 75%">
              <dx-color-box [(value)]="chartColour" [disabled]="chartColourDisabled"></dx-color-box>
            </div>
          </div>
        }
        <div class="target-section">
          <mat-form-field appearance="fill" class="half-field">
            <mat-label>Default Target Value</mat-label>
            <input formControlName="targetValue" type="number" min="0" matInput />
          </mat-form-field>
          @if (isMetric && form.controls.targetValue.value !== null) {
            <mat-form-field appearance="fill" class="target-comparison-field">
              <mat-label>Target should be</mat-label>
              <mat-select formControlName="targetComparison">
                @for (option of targetComparisonOptions; track option.value) {
                  <mat-option [value]="option.value">{{ option.label }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
          }
          @if ((!isMetric || hasMetricSplit) && !isDraft) {
            <div
              class="customise-target-control"
              [matTooltip]="
                hasUnsavedSignificantChanges
                  ? 'Save changes first to enable target customisation - existing config will be wiped!'
                  : ''
              ">
              <button
                mat-stroked-button
                type="button"
                class="customise-target-btn"
                (click)="toggleCustomiseTarget()"
                [disabled]="!canCustomiseTarget()">
                {{ showCustomTargets ? 'Hide Custom Targets' : 'Customise Targets' }}
              </button>
            </div>
          }
        </div>
        @if (isMetric) {
          <div class="m-b-15 metric-description">
            <div class="flex-row-start-center m-b-8">
              <mat-checkbox formControlName="showDescription" class="checkbox"></mat-checkbox>
              <span class="m-l-8">Show description (larger metrics only)</span>
            </div>
            @if (form.controls.showDescription.value) {
              <mat-form-field appearance="fill" class="full-width">
                <mat-label>Description</mat-label>
                <textarea
                  matInput
                  formControlName="description"
                  maxlength="{{ MAX_DESC_LENGTH }}"
                  rows="2"></textarea>
                <mat-hint align="end"
                  >{{ form.controls.description.value?.length || 0 }}/{{
                    MAX_DESC_LENGTH
                  }}</mat-hint
                >
              </mat-form-field>
            }
          </div>
        }
        @if (showCustomTargets) {
          <div class="custom-targets-section">
            <div class="custom-targets-header">
              <span class="custom-targets-title"
                >{{ isMetric ? 'Per-Series' : 'Per-Grouping' }} Target Overrides</span
              >
              <button
                mat-stroked-button
                type="button"
                class="clear-targets-btn"
                (click)="clearAllTargetOverrides()"
                [disabled]="!targetPeriods.length">
                Clear All
              </button>
            </div>
            @if (loadingTargetPeriods) {
              <div class="info-box-sml m-b-10">Loading grouping values...</div>
            }
            @if (!loadingTargetPeriods && !targetPeriods.length) {
              <div class="info-box-sml m-b-10">
                No data found for the current {{ isMetric ? 'metric' : 'chart' }} configuration.
              </div>
            }
            @if (!loadingTargetPeriods && targetPeriods.length) {
              <div class="custom-targets-table">
                <div class="custom-targets-row custom-targets-row-header">
                  <div class="custom-targets-cell period-cell">
                    {{ isMetric ? 'Series' : 'Grouping' }}
                  </div>
                  <div class="custom-targets-cell value-cell">Target</div>
                </div>
                @for (period of targetPeriods; track period; let i = $index) {
                  <div class="custom-targets-row">
                    <div class="custom-targets-cell period-cell">{{ period.label }}</div>
                    <div class="custom-targets-cell value-cell">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        [value]="period.value"
                        (input)="onPeriodTargetChange(i, $event)"
                        placeholder="Default" />
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
      @if (dataSourceFilterFields.length) {
        <div class="section p-b-15 m-b-0">
          <div class="filter-label">
            Filters
            @if (isForecastTasksInProgress) {
              <span class="filter-label-suffix"> (applies to both task types)</span>
            }
          </div>
          <dx-filter-builder
            [disabled]="!form.controls.datasource.value"
            [fields]="dataSourceFilterFields"
            [(value)]="filter"
            class="filter"
            (onValueChanged)="onFilterChanged()">
          </dx-filter-builder>
        </div>
      }
    }
  </mat-dialog-content>
} @else {
  <mat-dialog-content class="p-t-20">
    @if (jobFieldsError) {
      <div class="error-box-sml m-b-15">Job fields could not be loaded. Please try again.</div>
    } @else {
      <app-spinner [inline]="true" heightPx="78"></app-spinner>
    }
  </mat-dialog-content>
}

<div mat-dialog-actions align="end" class="modal-footer">
  <button mat-stroked-button mat-dialog-close>Cancel</button>
  @if (!isDraft) {
    <button mat-stroked-button color="warn" (click)="delete()" [disabled]="loading">Delete</button>
  }
  <button
    mat-stroked-button
    color="primary"
    (click)="save()"
    [disabled]="saveDisabled">
    Save
  </button>
</div>
